import { useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChevronDown, ChevronRight, Microscope, Plus, Play, Save, CheckCircle2, Trash2, User, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { ArtifactList } from '@/components/artifacts/ArtifactList';
import { ArtifactUploader } from '@/components/artifacts/ArtifactUploader';
import { ArtifactLinkCapture } from '@/components/artifacts/ArtifactLinkCapture';
import { LINK_ENTITY_TYPES, LINK_ROLES } from '@/lib/artifacts/constants';
import type { WorkflowDiagnostic, WorkflowDiagnosticTest } from '@/hooks/useWorkflowReport';

interface WorkflowDiagnosticsSectionProps {
  diagnostics: WorkflowDiagnostic[];
  canEdit: boolean;
  latestComplaint?: string;
  onCreate: (concern?: string) => void;
  onUpdate: (id: string, updates: Record<string, any>) => void;
  onStart: (id: string) => void;
  onComplete: (id: string) => void;
  onAddTest: (diagnosticId: string, testName: string) => void;
  onUpdateTest: (id: string, updates: Record<string, any>) => void;
  onRemoveTest: (id: string) => void;
  shopId?: string;
}

function formatDuration(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
}

export function WorkflowDiagnosticsSection({
  diagnostics, canEdit, latestComplaint, onCreate, onUpdate, onStart, onComplete,
  onAddTest, onUpdateTest, onRemoveTest, shopId,
}: WorkflowDiagnosticsSectionProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(diagnostics[0]?.id || 'new');
  const [newTestName, setNewTestName] = useState('');

  const activeDiag = diagnostics.find(d => d.id === activeTab);
  const isActive = activeDiag && activeDiag.started_at && !activeDiag.completed_at;
  const isCompleted = !!activeDiag?.completed_at;
  const canModify = canEdit && isActive;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 rounded-lg border">
          <div className="flex items-center gap-2">
            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <Microscope className="h-4 w-4 text-purple-600" />
            <span className="font-semibold text-sm">Diagnostics</span>
            <Badge variant="secondary" className="text-xs">{diagnostics.length}</Badge>
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-4 pt-2 pb-4 space-y-4">
          {diagnostics.length > 0 ? (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="flex flex-wrap h-auto gap-1">
                {diagnostics.map(d => (
                  <TabsTrigger key={d.id} value={d.id} className="text-xs">
                    {d.title || 'Untitled'}
                    {d.completed_at && <CheckCircle2 className="h-3 w-3 ml-1 text-green-500" />}
                  </TabsTrigger>
                ))}
                {canEdit && (
                  <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => onCreate(latestComplaint)}>
                    <Plus className="h-3 w-3 mr-1" />New
                  </Button>
                )}
              </TabsList>

              {diagnostics.map(diag => (
                <TabsContent key={diag.id} value={diag.id} className="space-y-4 mt-4">
                  {/* Session info */}
                  <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                    {diag.performer_name && (
                      <span className="flex items-center gap-1"><User className="h-3 w-3" />Performed by: {diag.performer_name}</span>
                    )}
                    {diag.completed_at && (
                      <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Completed: {format(new Date(diag.completed_at), 'MMM d, h:mm a')}</span>
                    )}
                    {diag.duration_seconds != null && (
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Duration: {formatDuration(diag.duration_seconds)}</span>
                    )}
                  </div>

                  {/* Diagnostic form */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Concern</Label>
                      <Textarea value={diag.concern || ''} onChange={e => canModify && onUpdate(diag.id, { concern: e.target.value })} rows={2} disabled={!canModify} className="text-xs" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Conditions</Label>
                      <Textarea value={diag.conditions || ''} onChange={e => canModify && onUpdate(diag.id, { conditions: e.target.value })} rows={2} disabled={!canModify} className="text-xs" />
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Label className="text-xs">Complaint Confirmed</Label>
                    <Switch checked={diag.complaint_confirmed} onCheckedChange={val => canModify && onUpdate(diag.id, { complaint_confirmed: val })} disabled={!canModify} />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">DTC List</Label>
                      <Textarea value={diag.dtc_list || ''} onChange={e => canModify && onUpdate(diag.id, { dtc_list: e.target.value })} rows={2} disabled={!canModify} className="text-xs" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Scan Details</Label>
                      <Textarea value={diag.scan_details || ''} onChange={e => canModify && onUpdate(diag.id, { scan_details: e.target.value })} rows={2} disabled={!canModify} className="text-xs" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">TSB References</Label>
                      <Textarea value={diag.tsb_refs || ''} onChange={e => canModify && onUpdate(diag.id, { tsb_refs: e.target.value })} rows={2} disabled={!canModify} className="text-xs" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Service Info Refs</Label>
                      <Textarea value={diag.service_info_refs || ''} onChange={e => canModify && onUpdate(diag.id, { service_info_refs: e.target.value })} rows={2} disabled={!canModify} className="text-xs" />
                    </div>
                  </div>

                  {/* Tests */}
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold">Tests</Label>
                    {diag.tests.map(test => (
                      <div key={test.id} className="border rounded-lg p-2 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium">{test.test_name}</span>
                          {canModify && (
                            <Button size="sm" variant="ghost" className="h-5 w-5 p-0" onClick={() => onRemoveTest(test.id)}>
                              <Trash2 className="h-3 w-3 text-destructive" />
                            </Button>
                          )}
                        </div>
                        <Input
                          value={test.result || ''}
                          onChange={e => canModify && onUpdateTest(test.id, { result: e.target.value })}
                          placeholder="Result"
                          className="text-xs h-7"
                          disabled={!canModify}
                        />
                        <Input
                          value={test.notes || ''}
                          onChange={e => canModify && onUpdateTest(test.id, { notes: e.target.value })}
                          placeholder="Notes"
                          className="text-xs h-7"
                          disabled={!canModify}
                        />
                      </div>
                    ))}
                    {canModify && (
                      <div className="flex gap-2">
                        <Input value={newTestName} onChange={e => setNewTestName(e.target.value)} placeholder="Test name..." className="text-xs h-7" />
                        <Button size="sm" className="h-7" onClick={() => { if (newTestName.trim()) { onAddTest(diag.id, newTestName.trim()); setNewTestName(''); } }}>
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Findings</Label>
                      <Textarea value={diag.findings || ''} onChange={e => canModify && onUpdate(diag.id, { findings: e.target.value })} rows={2} disabled={!canModify} className="text-xs" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Root Cause</Label>
                      <Textarea value={diag.root_cause || ''} onChange={e => canModify && onUpdate(diag.id, { root_cause: e.target.value })} rows={2} disabled={!canModify} className="text-xs" />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">Recommendation</Label>
                    <Textarea value={diag.recommendation || ''} onChange={e => canModify && onUpdate(diag.id, { recommendation: e.target.value })} rows={2} disabled={!canModify} className="text-xs" />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">Confidence (1-5)</Label>
                    <Select value={String(diag.confidence || '')} onValueChange={val => canModify && onUpdate(diag.id, { confidence: parseInt(val) })} disabled={!canModify}>
                      <SelectTrigger className="w-24 h-7 text-xs">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5].map(v => <SelectItem key={v} value={String(v)}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Diagnostic-level artifacts */}
                  {shopId && (
                    <>
                      <ArtifactList
                        shopId={shopId}
                        entityType={LINK_ENTITY_TYPES.WORKFLOW_DIAGNOSTIC}
                        entityId={diag.id}
                        canEdit={canEdit}
                      />
                      {canEdit && (
                        <div className="flex items-center gap-1">
                          <ArtifactUploader
                            shopId={shopId}
                            entityType={LINK_ENTITY_TYPES.WORKFLOW_DIAGNOSTIC}
                            entityId={diag.id}
                            role={LINK_ROLES.DIAGNOSTIC_EVIDENCE}
                          />
                          <ArtifactLinkCapture
                            shopId={shopId}
                            entityType={LINK_ENTITY_TYPES.WORKFLOW_DIAGNOSTIC}
                            entityId={diag.id}
                            role={LINK_ROLES.DIAGNOSTIC_EVIDENCE}
                          />
                        </div>
                      )}
                    </>
                  )}

                  {/* Session controls */}
                  <div className="flex gap-2 pt-2 border-t">
                    {!diag.started_at && canEdit && (
                      <Button size="sm" onClick={() => onStart(diag.id)}>
                        <Play className="h-3 w-3 mr-1" />Start Diag
                      </Button>
                    )}
                    {isActive && diag.id === activeTab && canEdit && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => onUpdate(diag.id, {})}>
                          <Save className="h-3 w-3 mr-1" />Save
                        </Button>
                        <Button size="sm" onClick={() => onComplete(diag.id)}>
                          <CheckCircle2 className="h-3 w-3 mr-1" />Mark Complete
                        </Button>
                      </>
                    )}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          ) : (
            canEdit && (
              <Button size="sm" variant="outline" onClick={() => onCreate(latestComplaint)}>
                <Plus className="h-3 w-3 mr-1" />New Diagnostic
              </Button>
            )
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
